/**
 * MemoForm's editable value state (title / body / due / owner / stage /
 * privacy) plus the note-identity reset that re-seeds it when the form is
 * pointed at a different record without remounting.
 *
 * Extracted from MemoForm so the form component reads as layout + submit
 * wiring, and so the seeding rules — which differ per field between create
 * and edit mode, and which a stray re-seed can turn into silent data loss —
 * live together where they can be compared.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { TimeValue } from 'react-aria-components';

import { computeInitialDue } from './memoFormValues';
import type { MemoPrivacy } from './notes.utils';
import { memoPrivacy } from './notes.utils';
import type { Note } from './useMemoRecords';

export interface UseMemoFormStateOptions {
  mode: 'create' | 'edit';
  note: Note | undefined;
  /** `entityType === 'ahoy_note'` — notes take no title/due prefills. */
  isNote: boolean;
  /** Inline (record-slideout) variant: autosaves, so it must not re-seed. */
  isInline: boolean;
  currentUserEntityId: string | undefined;
  initialTitle: string | undefined;
  initialContent: string | undefined;
  initialDate: string | undefined;
  initialPrivacy: MemoPrivacy | undefined;
}

export interface MemoFormState {
  title: string;
  setTitle: (value: string) => void;
  content: string;
  setContent: (value: string) => void;
  date: string;
  setDate: (value: string) => void;
  time: TimeValue | null;
  setTime: (value: TimeValue | null) => void;
  ownerEntityId: string;
  setOwnerEntityId: (value: string) => void;
  /** `undefined` = no pending override; the saved stage stands. */
  stagePickerOverride: string | null | undefined;
  setStagePickerOverride: (value: string | null | undefined) => void;
  privacy: MemoPrivacy;
  setPrivacy: (value: MemoPrivacy) => void;
}

export function useMemoFormState({
  mode,
  note,
  isNote,
  isInline,
  currentUserEntityId,
  initialTitle,
  initialContent,
  initialDate,
  initialPrivacy,
}: UseMemoFormStateOptions): MemoFormState {
  // Empty initial title; the input's own placeholder ("Note title") guides the
  // user. Defaulting to the literal string 'Note' here was the source of the
  // "title row says Note, body shows the real content" noise the NoteCard now
  // suppresses by treating empty / 'Note' as "no title set".
  const [title, setTitle] = useState(
    note?.title ?? (isNote ? '' : (initialTitle ?? ''))
  );
  const [content, setContent] = useState(note?.content || initialContent || '');
  // User-editable due date + optional time. A date alone maps to ahoy_date;
  // a date with a time maps to ahoy_datetime (see combineDue / dueProperties).
  // Notes never take a create-mode date prefill (see isNote).
  const initialDue = computeInitialDue(
    note,
    isNote ? undefined : initialDate,
    mode
  );
  const [date, setDate] = useState<string>(initialDue.date);
  const [time, setTime] = useState<TimeValue | null>(initialDue.time);

  // Default owner:
  //  - edit mode: PRESERVE the existing owner exactly. Falling back
  //    to the current user here would silently re-assign ownership
  //    on save for notes that were intentionally unowned (or whose
  //    owner data simply hadn't propagated yet).
  //  - create mode: default to the current user.
  // Empty string when neither is known (e.g. an unauth'd preview).
  const [ownerEntityId, setOwnerEntityIdInternal] = useState<string>(
    mode === 'edit'
      ? (note?.owner_entity_id ?? '')
      : (currentUserEntityId ?? '')
  );
  // Marks whether the user has explicitly chosen an owner since the last note
  // reset. The create-mode default-owner sync effect (which fires when
  // `currentUserEntityId` resolves async) must NOT overwrite a value the user
  // already picked. Reset alongside the note identity below.
  const ownerEditedRef = useRef(false);
  // Public setter: any owner change coming from the UI (the owner picker) is a
  // user edit, so record it. Internal seeding/sync uses the raw setter.
  const setOwnerEntityId = useCallback((value: string) => {
    ownerEditedRef.current = true;
    setOwnerEntityIdInternal(value);
  }, []);
  // Pending stage selection from the metadata-bar stage picker (tasks only).
  // Reset to undefined whenever the note identity changes (see below).
  const [stagePickerOverride, setStagePickerOverride] = useState<
    string | null | undefined
  >(undefined);
  // Privacy / Visibility state. New memos default to 'shared' (visible to the
  // whole org) so collaborators see them by default. Editing an existing memo
  // preserves its saved visibility — `note.is_private` is the source of truth
  // (set by notes.utils::extractMemoData from the presence of the
  // ahoy_note_visible_to_org association). Only the owner or creator can flip
  // privacy after creation; MemoForm's canTogglePrivacy owns that gate.
  const [privacy, setPrivacy] = useState<MemoPrivacy>(
    note && memoPrivacy(note) === 'private'
      ? 'private'
      : (initialPrivacy ?? 'shared')
  );

  // Tracks the note identity across renders so the reset effect can tell a
  // genuine note-switch (must re-seed) from a same-note update (our own
  // optimistic autosave write — must NOT re-seed, or it clobbers the field
  // being typed). Only consulted in the inline autosave variant; see the guard
  // at the top of the reset effect below.
  const prevEntityIdRef = useRef<string | undefined>(undefined);

  // Sync state when the note prop changes (e.g. switching to a different note
  // without remounting). Key on entity_id so a switch between notes with
  // identical title/content still triggers a reset.
  // `initialStageEntityId` and the other `initial*` create-mode prefills are
  // intentionally NOT in the dep list — async resolution (e.g. waiting on the
  // default-pipeline query) would otherwise wipe user edits in progress. The
  // async stage prefill is handled by MemoForm so it only sets stage state,
  // not title/content/dates. Reading the `initial*` props from closure here is
  // the same pattern — they seed state on the initial mount run of this effect
  // and are not re-read on subsequent runs.
  useEffect(() => {
    const entityChanged = prevEntityIdRef.current !== note?.entity_id;
    prevEntityIdRef.current = note?.entity_id;
    // Inline autosave guard: a same-note update is this form's OWN optimistic
    // write (autosave persisted, the parent bumped the note). Re-seeding here
    // would overwrite the field the user is still typing. So in the inline
    // variant, re-seed only on a genuine note switch (or first mount). The
    // modal variants (non-inline) keep the original per-field re-seed — their
    // deps below are unchanged, so no modal behavior changes.
    if (isInline && !entityChanged) {
      return;
    }
    // In create mode (note undefined) fall back to `initial*` prefills so the
    // values seeded by useState aren't wiped to '' when this effect runs on
    // mount. In edit mode the note is the source of truth.
    setTitle(
      note?.title ?? (mode === 'create' && !isNote ? (initialTitle ?? '') : '')
    );
    setContent(
      note?.content || (mode === 'create' ? (initialContent ?? '') : '')
    );
    const resetDue = computeInitialDue(
      note,
      isNote ? undefined : initialDate,
      mode
    );
    setDate(resetDue.date);
    setTime(resetDue.time);
    // A note switch (or first mount) is a fresh form: forget any prior owner
    // edit so the create-mode default-owner sync effect below can seed again.
    ownerEditedRef.current = false;
    // Mirror the initializer: in edit mode, preserve the existing owner
    // exactly (do NOT fall back to the current user, which would silently
    // re-assign ownership of intentionally unowned notes on save). In create
    // mode seed from whatever `currentUserEntityId` is known RIGHT NOW; if it
    // hasn't resolved yet the owner-sync effect below fills it in when it does.
    // `currentUserEntityId` is intentionally NOT a dep of this reset effect —
    // its async arrival must not re-run the full reset and wipe fields the user
    // is typing (see the dedicated sync effect below).
    setOwnerEntityIdInternal(
      mode === 'edit'
        ? (note?.owner_entity_id ?? '')
        : (currentUserEntityId ?? '')
    );
    // Reset privacy to match the freshly-loaded note. Without this a switch
    // between notes would carry the previous note's privacy forward, silently
    // flipping the destination note on the next save. In create mode fall back
    // to `initialPrivacy` so the seeded value isn't wiped to 'shared'.
    if (note && memoPrivacy(note) === 'private') {
      setPrivacy('private');
    } else if (mode === 'create') {
      setPrivacy(initialPrivacy ?? 'shared');
    } else {
      setPrivacy('shared');
    }
    // Clear any pending stage override so a previous edit on one task
    // doesn't leak into the next task opened in the same mounted form.
    setStagePickerOverride(undefined);
    // `initial*` props and `currentUserEntityId` are intentionally excluded
    // from the dep list — see the comment above the effect and the dedicated
    // owner-sync effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    note?.entity_id,
    note?.title,
    note?.content,
    note?.date,
    note?.datetime,
    note?.owner_entity_id,
    note?.is_private,
    mode,
    isInline,
    isNote,
  ]);

  // Create-mode default owner: when `currentUserEntityId` resolves async (org
  // membership arriving after the form mounted), seed the owner to the current
  // user — but ONLY while the owner is still the create-mode default (empty and
  // unedited). This is decoupled from the reset effect above so the async
  // arrival sets just the owner and never re-runs the full field reset that
  // would discard input the user has already started typing. Edit mode is
  // untouched: it preserves the note's saved owner exactly.
  useEffect(() => {
    if (mode !== 'create' || !currentUserEntityId) {
      return;
    }
    if (ownerEditedRef.current || ownerEntityId !== '') {
      return;
    }
    setOwnerEntityIdInternal(currentUserEntityId);
  }, [mode, currentUserEntityId, ownerEntityId]);

  return {
    title,
    setTitle,
    content,
    setContent,
    date,
    setDate,
    time,
    setTime,
    ownerEntityId,
    setOwnerEntityId,
    stagePickerOverride,
    setStagePickerOverride,
    privacy,
    setPrivacy,
  };
}
